<script lang="ts" setup>
import { Direction } from '@enums/direction.ts';
import type { MentionableUser } from '@models/interfaces/mentionableUser';
import { ref, computed, getCurrentInstance, watch, onMounted, onBeforeUnmount } from 'vue';
import { QuillEditor } from '@vueup/vue-quill';
import Quill from 'quill';
import type { QuillEditor as QuillEditorType } from '@vueup/vue-quill';
import Button from '@components/Button.vue';
import HorizontalList from '@components/HorizontalList.vue';
import { eventBus } from '@tools/event-bus';
import { trans, transchoice } from '@tools/translation';
import UploadMultipleUserFile from '@components/UploadMultipleUserFile.vue';
import { useLoading } from '@composables/useLoading.ts';
import Icon from '@components/Icon.vue';
import IconPaperClip from '@icons/ph_paper-clip.svg';
import IconSmiley from '@icons/ph_smiley.svg';
import IconLogoAtMail from '@icons/ph_logo-at-mail.svg';
import IconPaperPlaneTiltFill from '@icons/ph_paper-plane-tilt-fill.svg';
import type { Attachment as AttachmentType } from '@global-types/attachment.ts';
import Attachment from '@components/Attachment.vue';
import { useModal } from '@composables/useModal.ts';
import EmojiPicker, { type EmojiExt } from 'vue3-emoji-picker';
import { UserMentionModule, QUILL_USER_MENTION_EVENT_SEARCH, QUILL_USER_MENTION_EVENT_EXIT } from './ContentVueEditor/userMentionModule.ts';
import MentionBox from './ContentVueEditor/MentionBox.vue';
import 'vue3-emoji-picker/css';

declare type QuillModule = {
  name: string;
  module: unknown;
  options?: object;
}

declare type QuillToolbarModule = {
  handlers: Record<string, (value?: unknown) => void>;
  addHandler?: (name: string, handler: (value?: unknown) => void) => void;
};

const {
  modelValue,
  disableLink = false,
  showSubmit = false,
  minCounter,
  maxCounter,
  placeholder,
  loading = false,
  attachments = [],
  users = [],
  enableMentions = false,
  enableEmojis = false,
  emojisPanelPosition = Direction.UP,
  enableAttachments = false,
} = defineProps<{
  modelValue: string;
  disableLink?: boolean;
  showSubmit?: boolean;
  minCounter?: number;
  maxCounter?: number;
  placeholder?: string;
  loading?: boolean;
  attachments?: AttachmentType[];
  users?: MentionableUser[];
  enableMentions?: boolean;
  enableEmojis?: boolean;
  emojisPanelPosition?: Direction.UP | Direction.DOWN;
  enableAttachments?: boolean;
}>();

const emit = defineEmits<{(e: 'update:modelValue', payload: string): void;
  (e: 'update:attachments', payload: AttachmentType[]): void;
  (e: 'submit'): void;
}>();

const instance = getCurrentInstance();
const uid = String(instance?.uid ?? Math.floor(Math.random() * 10000));
const toolbarId = `quill-toolbar-${uid}`;

const {
  isLoading: isUploading,
  startLoading: startUploading,
  stopLoading: stopUploading,
} = useLoading();

const {
  isOpen: isEmojiPickerOpen,
  toggleModal: toggleEmojiPicker,
  closeModal: closeEmojiPicker,
} = useModal();

const html = computed<string>({
  get: () => modelValue,
  set: (value: string) => emit('update:modelValue', value),
});

const editorContainer = ref<HTMLDivElement>();
const quillRef = ref<InstanceType<typeof QuillEditorType>>();
const quillInstance = computed<Quill>(() => quillRef.value!.getQuill());

const text = computed<string>(() => html.value.replace(/<[^>]*>|\r?\n/g, '').trim());

const hasCounter = computed<boolean>(() => maxCounter !== undefined || minCounter !== undefined);
const counter = computed<number>(() => text.value.length);
const canSubmit = computed<boolean>(() => (text.value.length > 0 || attachments.length > 0) && loading === false);

const modules = ref<QuillModule[]>([]);
const mention = ref(null);
if (enableMentions) {
  modules.value.push({
    name: 'user-mention',
    module: UserMentionModule,
  });

  eventBus.addEventListener(QUILL_USER_MENTION_EVENT_SEARCH, (event: CustomEvent) => {
    mention.value = event.detail.search;
  });

  eventBus.addEventListener(QUILL_USER_MENTION_EVENT_EXIT, () => {
    mention.value = null;
  });
}

const editorIsFocused = ref<boolean>(false);

watch(editorIsFocused, (value) => {
  if (!enableMentions) {
    return;
  }
  // The placeholder is implemented with a CSS rule in Quill, so :placeholder attribute is not dynamic, use native hack...
  const editorElement = editorContainer.value!.querySelector('.ql-editor');
  if (editorElement) {
    editorElement.setAttribute('data-placeholder', value ? trans('content_editor.mention.placeholder') : (placeholder ?? ''));
  }
});

const mentionUser = (user: MentionableUser) => {
  const mentionModule = quillInstance.value.getModule('user-mention') as UserMentionModule;
  mentionModule.selectUser(user);
};

const watchModelValue = (): void => {
  watch(() => modelValue, (value) => {
    if (value.trim().length === 0) {
      quillInstance.value?.setText('');
    }
  });
};

const setupLinkTool = (): void => {
  if (disableLink) {
    return;
  }
  const toolbar = quillInstance.value.getModule('toolbar') as QuillToolbarModule;
  toolbar.handlers.link = (): void => {
    const range = quillInstance.value.getSelection();
    if (!range) {
      return;
    }
    // eslint-disable-next-line no-alert
    const url = prompt(trans('content_editor.link.message'));
    if (url && /^https?:\/\//i.test(url)) {
      quillInstance.value.format('link', url);
    } else if (url) {
      // eslint-disable-next-line no-alert
      alert(trans('content_editor.link.error'));
    }
  };
};

const setupKeyListeners = (): void => {
  quillInstance.value.root.addEventListener('keydown', (e: KeyboardEvent) => {
    // Hack for implementation on search
    if (e.key === 'Enter') {
      setTimeout(() => quillInstance.value.focus(), 0);
    }
  });
};

const setupQuillContainer = (): void => {
  if (!showSubmit) {
    return;
  }

  const editorElement = editorContainer.value?.querySelector('.ql-editor');
  if (editorElement) {
    editorElement.classList.add('ql-editor--with-submit');
  }
};

const quillReady = (): void => {
  watchModelValue();
  setupLinkTool();
  setupKeyListeners();
  setupQuillContainer();
};

const submit = () => {
  emit('submit');
};

const addAttachments = (files: AttachmentType[]): void => {
  emit('update:attachments', [ ...attachments, ...files ]);
};

const removeAttachment = (index: number): void => {
  const updated = attachments.filter((_, i) => i !== index);
  emit('update:attachments', updated);
};

const handleSelectEmoji = (emoji: EmojiExt): void => {
  const selection = quillInstance.value.getSelection();
  const cursorPosition = selection ? selection.index : quillInstance.value.getLength();

  if (text.value === '') {
    quillInstance.value.setText('');
    quillInstance.value.insertText(0, emoji.i);
    quillInstance.value.setSelection(emoji.i.length);

    closeEmojiPicker();

    return;
  }

  quillInstance.value.insertText(cursorPosition, emoji.i);
  quillInstance.value.setSelection(cursorPosition + emoji.i.length);

  closeEmojiPicker();
};

const emojiPickerContainer = ref<HTMLDivElement | null>(null);

const handleClickOutsideEmojiPicker = (event: TouchEvent | MouseEvent): void => {
  if (!isEmojiPickerOpen.value || !emojiPickerContainer.value) {
    return;
  }

  if (emojiPickerContainer.value.contains(event.target as Node)) {
    return;
  }

  closeEmojiPicker();
};

onMounted(() => {
  document.addEventListener('click', handleClickOutsideEmojiPicker);
  document.addEventListener('touchstart', handleClickOutsideEmojiPicker);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutsideEmojiPicker);
  document.removeEventListener('touchstart', handleClickOutsideEmojiPicker);
});
</script>

<template>
  <div class="content-vue-editor">
    <div
      ref="editorContainer"
      class="content-vue-editor__quill editor-quill"
      :class="{ 'editor-quill--attachments': attachments.length > 0 }"
    >
      <QuillEditor
        ref="quillRef"
        v-model:content="html"
        :placeholder="placeholder"
        content-type="html"
        theme="snow"
        :toolbar="`#${toolbarId}`"
        :modules="modules"
        @ready="quillReady"
        @focus="editorIsFocused = true"
        @blur="editorIsFocused = false"
      >
        <template #toolbar>
          <div :id="toolbarId" class="editor__toolbar">
            <button class="ql-bold" />
            <button class="ql-italic" />
            <button class="ql-underline" />
            <button class="ql-list" value="bullet" />
            <button v-if="!disableLink" class="ql-link" />
          </div>
        </template>
      </QuillEditor>

      <MentionBox
        v-if="mention !== null"
        :mention="mention"
        :users="users"
        @select-user="mentionUser"
        @close="mention = null"
      />

      <div
        :class="[
          'content-vue-editor__bottom',
          'absolute bottom-0',
          'w-full',
          'px-4 py-2',
          'flex flex-col gap-y-4'
        ]"
      >
        <HorizontalList
          v-if="attachments.length > 0"
          class="relative"
        >
          <Attachment
            v-for="(attachment, index) in attachments"
            :key="attachment.uuid"
            class="flex-none"
            :file="attachment"
            @remove="removeAttachment(index)"
          />
        </HorizontalList>

        <div
          :class="[
            'content-vue-editor__actions',
            'w-full',
            'flex items-center justify-between',
          ]"
        >
          <div
            :class="[
              'content-vue-editor__actions--left',
              'flex items-center gap-x-2',
            ]"
          >
            <!--            @Todo : https://bureaux-a-partager.atlassian.net/browse/MATCH-10318 on sait pas si on le fera ce bouton-->
            <!--            <Button-->
            <!--              v-if="enableMentions"-->
            <!--              class="content-vue-editor__action"-->
            <!--              variant="circle"-->
            <!--            >-->
            <!--              <Icon-->
            <!--                :svg="IconLogoAtMail"-->
            <!--                height="24"-->
            <!--                width="24"-->
            <!--              />-->
            <!--            </Button>-->

            <div
              v-if="enableEmojis"
              ref="emojiPickerContainer"
              class="relative"
            >
              <Button
                class="content-vue-editor__action"
                variant="circle"
                @click="toggleEmojiPicker"
              >
                <Icon
                  :svg="IconSmiley"
                  height="24"
                  width="24"
                />
              </Button>

              <EmojiPicker
                v-show="isEmojiPickerOpen"
                :class="[
                  'content-vue-editor__emoji-picker absolute z-20',
                  emojisPanelPosition === Direction.UP ? 'bottom-14' : 'top-14',
                ]"
                :native="true"
                :static-texts="{
                  placeholder: trans('content_editor.emoji.search.placeholder'),
                }"
                hide-group-icons
                disable-skin-tones
                hide-group-names
                @select="handleSelectEmoji"
              />
            </div>

            <UploadMultipleUserFile
              v-if="enableAttachments"
              class="content-vue-editor__action"
              @loading="startUploading"
              @done="stopUploading"
              @input="addAttachments"
            >
              <Icon
                :svg="IconPaperClip"
                height="24"
                width="24"
              />
            </UploadMultipleUserFile>

            <slot name="actions" />
          </div>

          <div class="content-vue-editor__actions--right">
            <Button
              v-if="showSubmit"
              variant="primary"
              class="content-vue-editor__action--submit flex !h-10 !w-10 items-center !p-0"
              :disabled="!canSubmit"
              @click="submit"
            >
              <Icon
                :svg="IconPaperPlaneTiltFill"
                width="24"
                height="24"
                :fill="canSubmit ? 'white' : 'gray-darker'"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="hasCounter" class="content-vue-editor__counter editor-counter">
      {{ transchoice('global.char_counter', { count: counter }) }}
      <template v-if="minCounter != undefined">
        • {{ trans('global.min_counter', { count: minCounter }) }}
      </template>
      <template v-if="maxCounter != undefined">
        • {{ trans('global.max_counter', { count: maxCounter }) }}
      </template>
    </div>
  </div>
</template>

<style lang="scss">
@use '@styles/components/content-vue-editor';

.content-vue-editor {
  &__action {
    @apply w-10 h-10;

    @apply p-0;

    @apply flex justify-center items-center;
  }

  &__emoji-picker {
    .v3-footer {
      @apply hidden;
    }
  }
}

.ql-editor {
  @apply max-h-96;
}

.editor-quill--attachments {
  .ql-editor {
    @apply h-4/5 !important;

    @apply mb-16;
  }
}

.ql-container {
  @apply pb-10;
}
</style>
